import { Immutable } from "immer";
import React from "react";

import type { MultiTabInfoBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

type Props = Immutable<{
    value: Value;
}>;

// Mirrors the Django ``{% cycle 'back-sleeper' 'side-sleeper'
// 'stomach-sleeper' %}`` tag — the modifier cycles per-tab in this order.
const TAB_CYCLE = [
    "tab-back-sleeper",
    "tab-side-sleeper",
    "tab-stomach-sleeper",
];
const CONTENTS_CYCLE = ["back-sleeper", "side-sleeper", "stomach-sleeper"];

/**
 * React port of the pillows-specific ``MultiTabInfoBlock`` (defined in
 * ``server/cms/components/multi_tab_info.py``). DOM mirrors the legacy
 * Django template ``cms/components/multi_tab_info.html`` exactly.
 *
 * The legacy template had no JavaScript tab-switching (the CSS toggles
 * visibility purely via the ``--active`` modifier, which nothing ever
 * moved). To preserve parity it renders statically: the first tab and the
 * first contents pane carry ``--active``; everything else is hidden by the
 * stylesheet. The sleeper modifier classes cycle in template order.
 */
export const MultiTabInfoBlock = (props: Props): React.JSX.Element => {
    const v = props.value;
    const infoTabs = v.info_tabs ?? [];
    return (
        <div className="l-capped-width multi-tab-info">
            <div className="multi-tab-info__inner">
                <div className="multi-tab-info__title">{v.title ?? ""}</div>
                <div className="multi-tab-info__desc">
                    <RichTextBlock value={v.description ?? ""} />
                </div>
            </div>
            <ul className="multi-tab-info__tabs-container">
                {infoTabs.map((tab, i) => (
                    <li
                        key={i}
                        className={concatClassNames([
                            "multi-tab-info__tabs-container__tab",
                            `multi-tab-info__tabs-container__tab--${
                                TAB_CYCLE[i % TAB_CYCLE.length]
                            }`,
                            i === 0
                                ? "multi-tab-info__tabs-container__tab--active"
                                : "",
                        ])}
                    >
                        {tab.tab_title ?? ""}
                    </li>
                ))}
            </ul>
            {infoTabs.map((tab, i) => {
                const infoProps = tab.info_props ?? [];
                const imageUrl = tab.side_image?.url ?? "";
                return (
                    <div
                        key={i}
                        className={concatClassNames([
                            "multi-tab-info__contents",
                            `multi-tab-info__contents--${
                                CONTENTS_CYCLE[i % CONTENTS_CYCLE.length]
                            }`,
                            i === 0 ? "multi-tab-info__contents--active" : "",
                        ])}
                    >
                        <div className="multi-tab-info__image">
                            <img
                                className="responsive-img"
                                src={imageUrl}
                                alt={
                                    tab.side_image?.default_alt_text ||
                                    tab.side_image?.title ||
                                    ""
                                }
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                        <div className="multi-tab-info__pane-contents">
                            {infoProps.map((prop, j) => (
                                <React.Fragment key={j}>
                                    <div className="multi-tab-info__pane-contents__title">
                                        {prop.title ?? ""}
                                    </div>
                                    <div className="multi-tab-info__pane-contents__text">
                                        {prop.body_text ?? ""}
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
